<template>
  <div>
    <router-view />
    <div
      v-if="orders.length"
      :class="$style.header"
    >
      <BaseButton
        @click="onCreateInvoiceClick"
        v-test="'customer-create-sale'"
      >
        {{ $t(`global.actions.create_${orderKey()}`) }}
      </BaseButton>
    </div>
    <BaseCard v-if="orders.length">
      <BaseTable
        :headers="
          [
            $t('global.number'),
            $t('global.date_time'),
            hasSingleEmployee ? '' : $t('global.items.employee', 2),
            $t('global.total'),
            $t('global.status')
          ].filter((header) => !!header)
        "
        :rows="
          orders.map((order) => ({
            id: order.id,
            routerLink: {
              name: 'customer-sale',
              params: {
                id: order.id
              }
            },
            cells: [
              order.number,
              {
                value: filters.date(order.invoicedAt),
                type: 'date'
              },
              hasSingleEmployee ? undefined : order.employeeNames,
              filters.currency(order.total),
              {
                label: order.state.toLowerCase()
              }
            ].filter((cell) => cell !== undefined)
          }))
        "
        v-test="'customer-orders'"
      />
    </BaseCard>
    <LoadMore
      v-show="!allDataFetched && firstFetchFinished"
      @shown="fetchMore"
    />
    <BaseSpinner
      v-show="!firstFetchFinished"
      inline
    />
    <EmptyPageContent
      v-if="!loading && !orders.length"
      imageName="invoices"
      :text="{
        title: $t(`customers.empty_state.${orderKey('plural')}.title`),
        description: $t(`customers.empty_state.${orderKey('plural')}.description`),
        btnPrimary: $t(`global.actions.create_${orderKey()}`)
      }"
      :moreLink="{
        text: $t(`${orderKey('plural')}.empty_state.more_text`),
        url: helpCenterCollectionUrl($t(`${orderKey('plural')}.empty_state.more_url`))
      }"
      @click="onCreateInvoiceClick"
    />
  </div>
</template>

<script
  setup
  lang="ts"
>
import gql from 'graphql-tag';
import { computed, inject } from 'vue';
import { useRouter } from 'vue-router';
import { usePagination } from '@/apollo/pagination';
import EmptyPageContent from '@/components/EmptyPageContent.vue';
import LoadMore from '@/components/LoadMore.vue';
import filters from '@/filters';
import { orderKey } from '@/helpers/compliant-register';
import { helpCenterCollectionUrl } from '@/helpers/help-center';
import { useResourcesStore } from '@/stores/resources';

const router = useRouter();
const customer = inject<any>('customer');

const { hasSingleEmployee } = useResourcesStore();

const GET_ORDERS = gql`
  query getOrders(
    $pagination: PaginationAttributes!
    $customerId: ID
    $direction: SortDirectionEnum = ASC
  ) {
    orders(
      pagination: $pagination
      customerId: $customerId
      direction: $direction
    ) {
      id
      invoicedAt
      employeeNames
      number
      state
      total
    }
  }
`;
const { orders, allDataFetched, loading, firstFetchFinished, fetchMore } =
  usePagination({
    fieldName: 'orders',
    query: GET_ORDERS,
    variables: {
      customerId: customer?.value?.id,
      direction: 'DESC',
    },
    options: {
      enabled: computed(() => !!customer?.value?.id),
    },
  });

const onCreateInvoiceClick = () => {
  router.push({ name: 'register', query: { customerId: customer.value.id } });
};
</script>

<style
  lang="scss"
  module
>
.header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: $spacing;
}
</style>
